import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ConfirmDialog from '../ConfirmDialog';
import { getReceiptUploadLabel } from '../../utils/receiptFormLabel';
import {
  X
} from 'lucide-react';

const FormSelection = ({
  campaignId,
  campaignName,
  campaignSlug,
  activeForm,
  allForms,
  formFields,
  api,
  setAllForms,
  setActiveForm,
  setFormFields,
  setForms,
  setSuccessMessage,
  setShowSuccess,
  setErrorMessage,
  setShowError
}) => {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [previewFields, setPreviewFields] = useState([]);
  const [previewFormDetail, setPreviewFormDetail] = useState(null);
  const [activateErrorDialog, setActivateErrorDialog] = useState({ isOpen: false, message: '' });
  const handleFormChange = async (e) => {
    const formId = e.target.value;
    if (formId) {
      try {
        await api.patch(`/admin/forms/${formId}/activate`, {
          campaign_id: parseInt(campaignId)
        });
        
        // Refresh forms
        const allFormsResponse = await api.get('/admin/forms');
        setAllForms(allFormsResponse.data.forms || []);
        
        const response = await api.get(`/admin/forms?campaign_id=${campaignId}`);
        setForms(response.data.forms || []);
        const active = response.data.forms?.find(f => f.is_active);
        setActiveForm(active || null);
        
        // Fetch form fields
        if (formId) {
          try {
            const fieldsResponse = await api.get(`/admin/forms/${formId}/fields`);
            setFormFields(fieldsResponse.data.fields || []);
          } catch (err) {
            console.error('Failed to fetch form fields:', err);
            setFormFields([]);
          }
        }
      } catch (err) {
        console.error('Failed to activate form:', err);
        setActivateErrorDialog({
          isOpen: true,
          message: err.response?.data?.error || err.message || 'Failed to activate form.'
        });
      }
    }
  };

  const handleCreateForm = async () => {
    const { value: formName, isConfirmed } = await Swal.fire({
      title: 'Create New Form',
      input: 'text',
      inputLabel: 'Enter a name for the new form',
      inputPlaceholder: 'e.g. Entry Form',
      showCancelButton: true,
      confirmButtonColor: '#0f766e',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Create',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return 'Please enter a form name';
        }
      }
    });
    if (isConfirmed && formName) {
      try {
        const response = await api.post('/admin/forms', {
          name: formName,
          description: `Form for ${campaignName || 'Campaign'}`,
          campaign_id: parseInt(campaignId)
        });
        
        // Refresh forms list
        const allFormsResponse = await api.get('/admin/forms');
        setAllForms(allFormsResponse.data.forms || []);
        
        // Activate the new form
        await api.patch(`/admin/forms/${response.data.form.id}/activate`, {
          campaign_id: parseInt(campaignId)
        });
        
        // Set as active form
        setActiveForm(response.data.form);
        setFormFields([]);
        
        setSuccessMessage('New form created and activated successfully!');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      } catch (err) {
        console.error('Failed to create form:', err);
        alert('Failed to create form: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleUnlinkForm = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to unlink this form from the campaign?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, unlink it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.patch(`/admin/forms/${activeForm.id}/deactivate`, {
          campaign_id: parseInt(campaignId)
        });
        
        // Refresh forms
        const response = await api.get(`/admin/forms?campaign_id=${campaignId}`);
        setForms(response.data.forms || []);
        setActiveForm(null);
        setFormFields([]);
        
        setSuccessMessage('Form has been unlinked successfully!');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      } catch (err) {
        console.error('Failed to unlink form:', err);
        await Swal.fire('Error', 'Failed to unlink form', 'error');
      }
    }
  };

  const handlePreview = async () => {
    try {
      const [detailRes, fieldsResponse] = await Promise.all([
        api.get(`/admin/forms/${activeForm.id}`),
        api.get(`/admin/forms/${activeForm.id}/fields`)
      ]);
      setPreviewFormDetail(detailRes.data.form || null);
      const fields = fieldsResponse.data.fields || [];
      
      // Parse JSON field options if they're strings
      const parsedFields = fields.map(field => ({
        ...field,
        field_options: typeof field.field_options === 'string' 
          ? JSON.parse(field.field_options) 
          : field.field_options
      }));
      
      // Define system fields
      const systemFields = [
        { id: 'system_name', field_type: 'text', field_name: 'name', field_label: 'Full Name', is_required: true, is_system: true, sort_order: 0 },
        { id: 'system_email', field_type: 'email', field_name: 'email', field_label: 'Email Address', is_required: true, is_system: true, sort_order: 1 },
        { id: 'system_phone', field_type: 'phone', field_name: 'phone', field_label: 'Phone Number', is_required: false, is_system: true, sort_order: 2 }
      ];
      
      // Combine and sort all fields
      const combinedFields = [...systemFields, ...parsedFields].sort((a, b) => a.sort_order - b.sort_order);
      setPreviewFields(combinedFields);
      setShowPreview(true);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setPreviewFormDetail(null);
      alert('Failed to load form preview');
    }
  };

  const renderPreviewField = (field) => {
    switch (field.field_type) {
      case 'phone':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <PhoneInput
              country={'au'}
              disabled={true}
              containerClass="pointer-events-none opacity-60 w-full"
              inputClass="w-full"
              inputStyle={{ width: '100%' }}
            />
          </div>
        );
        
      case 'text':
      case 'email':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <input
              type={field.field_type === 'email' ? 'email' : 'text'}
              disabled={true}
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
              placeholder={field.field_placeholder || ''}
            />
          </div>
        );
        
      case 'textarea':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <textarea
              disabled={true}
              rows={field.field_options?.rows || 4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
              placeholder={field.field_placeholder || ''}
            />
          </div>
        );

      case 'video_recorder':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-md bg-gray-50 p-6">
              <div className="aspect-video rounded-md bg-black/80 flex items-center justify-center text-white text-sm">
                Video recorder preview
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Users will be able to record a video and upload it only when the full form is submitted.
              </p>
            </div>
          </div>
        );
        
      case 'select':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <select
              disabled={true}
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
            >
              <option value="">Select an option</option>
              {field.field_options?.options?.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
          </div>
        );
        
      case 'radio':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <div className="space-y-2">
              {field.field_options?.options?.map((option, idx) => (
                <label key={idx} className="inline-flex items-center mr-4">
                  <input
                    type="radio"
                    disabled={true}
                    className="cursor-not-allowed"
                  />
                  <span className="ml-2">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );
        
      case 'checkbox':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <div className="space-y-2">
              {field.field_options?.options?.map((option, idx) => (
                <label key={idx} className="flex items-center">
                  <input
                    type="checkbox"
                    disabled={true}
                    className="cursor-not-allowed"
                  />
                  <span className="ml-2">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="p-8">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Forms</h3>
      <div className="space-y-6">
        {/* Form Selection Dropdown */}
        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Form for Campaign
            </label>
            {activeForm && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> To change the form, you need to unlink the current form first using the "Unlink Form" button below.
                </p>
              </div>
            )}
            <select
              value={activeForm?.id || ''}
              disabled={!!activeForm}
              onChange={handleFormChange}
              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent ${activeForm ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">-- Select a form --</option>
              {allForms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.form_name} {form.campaign_name ? `(${form.campaign_name})` : ''} {form.is_active && form.campaign_id === parseInt(campaignId) ? ' - Active' : ''}
                </option>
              ))}
            </select>
            {activeForm && (
              <p className="mt-2 text-sm text-gray-600">
                This form will be displayed on the campaign public page.
              </p>
            )}
          </div>
          {!activeForm && (
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleCreateForm}
                className="px-4 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 whitespace-nowrap"
              >
                Create New Form
              </button>
            </div>
          )}
        </div>

        {/* Current Form Details */}
        {activeForm ? (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">{activeForm.form_name}</h4>
                  {activeForm.description && (
                    <p className="text-gray-600 mb-4">{activeForm.description}</p>
                  )}
                  <div className="space-y-2 text-sm text-gray-500">
                    <p>Fields: {formFields.length || 0}</p>
                    <p>Responses: {activeForm.response_count || 0}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handlePreview}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                  >
                    Preview
                  </button>
                  <button
                    onClick={handleUnlinkForm}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                  >
                    Unlink Form
                  </button>
                </div>
              </div>
            </div>

          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-600">No form selected for this campaign.</p>
            <p className="text-sm text-gray-500 mt-2">Select a form from the dropdown above or create a new one.</p>
          </div>
        )}

      </div>

      {/* Failed to activate form dialog */}
      <ConfirmDialog
        isOpen={activateErrorDialog.isOpen}
        onClose={() => setActivateErrorDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={() => setActivateErrorDialog(prev => ({ ...prev, isOpen: false }))}
        title="Failed to activate form"
        message={activateErrorDialog.message}
        confirmText="OK"
        cancelText="Close"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Form Preview: {activeForm?.form_name}
              </h3>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewFormDetail(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-600">
                    This is a view-only preview of how the form will appear on the campaign page.
                  </p>
                </div>
                
                {/* Form Fields */}
                <div className="space-y-6">
                  {previewFields.map(field => renderPreviewField(field))}
                  
                  {/* Receipt Upload (if validate receipt is enabled) */}
                  <div>
                    <label className="block text-base font-medium mb-2">
                      {getReceiptUploadLabel(previewFormDetail || activeForm || {})}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md bg-gray-50">
                      <div className="space-y-1 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="text-sm text-gray-600">
                          <p className="font-medium">Click to upload receipt</p>
                          <p className="text-xs mt-1">PNG, JPG up to 10MB</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Receipt validation is required for this campaign
                    </p>
                  </div>
                  
                  {/* Terms and Submit Button (view only) */}
                  <div className="pt-4 border-t">
                    <div className="mb-4">
                      <label className="flex items-start">
                        <input
                          type="checkbox"
                          disabled={true}
                          className="mt-1 cursor-not-allowed"
                        />
                        <span className="ml-2 text-sm">
                          I agree to the terms and conditions <span className="text-red-500">*</span>
                        </span>
                      </label>
                    </div>
                    
                    <button
                      type="button"
                      disabled={true}
                      className="w-full py-3 px-4 bg-teal-600 text-white font-medium rounded-lg opacity-50 cursor-not-allowed"
                    >
                      Submit Entry
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FormSelection;
